import React, { useState } from 'react';
import {
  Actions,
  Async,
  FSpread,
  toAsyncFetched,
} from '@client/new/helpers/types';
import { useOtpBundlesModel } from '../model/otpBundles.model';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { isEmpty } from 'lodash';
import { ColorsVars } from '@design-system/vars.css';

import {
  filterBundles,
  getOfferLinks,
  ListFilter,
  toListFilterTabs,
  toOTPBundleItems,
} from './otpBundle.vm.mapper';
import { DropDownStatus } from '../view/dropDownMultiselect/dropDownMultiselect';
import toast from 'react-hot-toast';
import { EditOtpBundleVM, useEditOtpBundleVM } from './editOtpBundle.vm';

export type BundleCardItemFeatures = Array<{
  id: number;
  name: string;
  checked: boolean;
}>;

export type Submitting = 'submitting';
export type Standard = 'standard';

export type StandardBundleCardData = {
  type: Standard;
  title: string;
  price: string;
  details: string;
  status: {
    label: string;
    color: {
      text: ColorsVars;
      bg: ColorsVars;
    };
  };
  programs: Array<{
    title: string;
    tooltipItems?: string[];
  }>;
  content: Array<{
    title: string;
    tooltipItems?: string[];
  }>;
  features: BundleCardItemFeatures;
  isFree: boolean;
};

export type BundleCardItem = {
  onClick: () => void;
  menu: Array<{
    icon: IconProp;
    disabled: boolean;
    onClick?: () => void;
    tooltip?: string | string[];
  }>;
  data: { type: Submitting; title: string } | StandardBundleCardData;
};

export type FormDataMultiSelect<T> = {
  sectionTitle: string;
  emptyState: {
    urlLabel: string;
    label: string;
    onClick: () => void;
  };
  options: Array<{
    name: string;
    id: T;
  }>;
  toFormOnChange: (value: T, selectedValues: T[]) => T[];
  selectedItems: Array<{
    name: string;
    id: T;
    disableDelete: boolean;
    /**
     * Optional status indicator (programs section only). When present the
     * View renders a `StatusTag` next to the item name with the same
     * Draft/Published pill + readiness layer as the picker dropdown.
     * Features/content sections omit this — their selected lists stay
     * a plain name + delete row.
     */
    status?: DropDownStatus;
  }>;
};

type Links = Array<{
  title: string;
  url: string;
  description: React.ReactNode;
  recommended?: boolean;
  copy: () => void;
  edit?: () => void;
}>;

export type OtpBundlesVMData = {
  add: () => void;
  emptyState: {
    show: boolean;
    addBundle: () => void;
  };
  list: {
    activeTab: string;
    changeTab: (value: string) => void;
    items: Array<BundleCardItem>;
    filterTabs: Array<ListFilter>;
  };
  linksModal: {
    showLinks: boolean;
    data: {
      links: Links;
    };
    isOpen: boolean;
    onClose: () => void;
  };
} & Pick<
  EditOtpBundleVM,
  | 'archiveModal'
  | 'deleteModal'
  | 'publishModal'
  | 'formData'
  | 'unsavedChangesModal'
>;

type PayloadMap = {
  setOtpTab: string;
  openLinksModal: number;
  closeLinksModal: null;
  copyToClipboard: string;
  editBundle: number;
  duplicateBundle: number;
  editLandingPage: number;
};

export type BundlesVMAction = Actions<PayloadMap>;
export type BundlesVMDispatcher = FSpread<PayloadMap>;

export type OtpBundlesVM = OtpBundlesVMData & {
  dispatch: BundlesVMDispatcher;
};

export type BundleVMState = {
  activeOtpTab: string;
  bundleLinksModal: number | null;
};

export const useOtpBundlesVM = ({
  creatorId,
}: {
  creatorId: number;
}): Async<OtpBundlesVM> => {
  const [state, setState] = useState<BundleVMState>({
    activeOtpTab: 'All',
    bundleLinksModal: null,
  });

  const model = useOtpBundlesModel({ creatorId });

  const {
    submittedNewBundle,
    submittedEditBundle,
    addOtpBundle,
    edit,
    duplicate,
    editLP,
    ...editBundleVM
  } = useEditOtpBundleVM({ creatorId, model });

  if (model.type !== 'fetched') return model;

  const { allPrograms, otpBundles, allFeatures, allContent } = model.value;

  const dispatch: BundlesVMDispatcher = (type, ...args) => {
    const action = { type, payload: args[0] } as BundlesVMAction;
    switch (action.type) {
      case 'setOtpTab':
        setState((prev) => ({
          ...prev,
          activeOtpTab: action.payload,
        }));
        return null;
      case 'openLinksModal':
        setState((prev) => ({
          ...prev,
          bundleLinksModal: action.payload,
        }));
        return null;
      case 'closeLinksModal':
        setState((prev) => ({
          ...prev,
          bundleLinksModal: null,
        }));
        return null;
      case 'copyToClipboard':
        navigator.clipboard.writeText(action.payload);
        toast.success('Link was copied to clipboard');
        return null;
      case 'editBundle':
        edit(action.payload);
        return null;
      case 'duplicateBundle':
        duplicate(action.payload);
        return null;
      case 'editLandingPage':
        editLP(action.payload);
        return null;
    }
  };

  const filteredBundles = filterBundles(state.activeOtpTab, otpBundles);

  return toAsyncFetched<OtpBundlesVM>({
    dispatch,
    add: addOtpBundle,
    emptyState: {
      show: isEmpty(otpBundles),
      addBundle: addOtpBundle,
    },
    list: {
      filterTabs: toListFilterTabs(otpBundles),
      activeTab: state.activeOtpTab,
      changeTab: (value: string) => dispatch('setOtpTab', value),
      items: toOTPBundleItems(
        filteredBundles,
        allPrograms,
        allFeatures,
        allContent,
        dispatch,
        submittedNewBundle,
        submittedEditBundle,
      ),
    },
    linksModal: {
      showLinks: true,
      isOpen: !!state.bundleLinksModal,
      onClose: () => dispatch('closeLinksModal'),
      data: {
        links: getOfferLinks(
          dispatch,
          otpBundles.find((b) => b.id === state.bundleLinksModal),
        ),
      },
    },
    ...editBundleVM,
  });
};
